/* Client-facing SMM (Social Media Marketing) catalog.
   Lets clients browse services grouped by parent category / sub-category,
   place an order paid from their platform balance, and review past orders.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Search, ShoppingBag, Clock3, CheckCircle2, XCircle,
  RefreshCcw, ChevronRight, Wallet, X, Send,
} from "lucide-react";

const fmtUSD = (htg, rate) => {
  if (!rate || rate <= 0) return "—";
  return `$${(Number(htg || 0) / rate).toFixed(2)}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const STATUS_LABEL = {
  pending: { label: "En attente", bg: "#FEF3C7", color: "#92400E", Icon: Clock3 },
  in_progress: { label: "En cours", bg: "#DBEAFE", color: "#1E40AF", Icon: RefreshCcw },
  completed: { label: "Livré", bg: "#DCFCE7", color: "#166534", Icon: CheckCircle2 },
  cancelled: { label: "Annulé", bg: "#E2E8F0", color: "#334155", Icon: XCircle },
  refunded: { label: "Remboursé", bg: "#FEE2E2", color: "#991B1B", Icon: XCircle },
};

export default function ClientSMM({ apiCall, clientBalanceHtg, exchangeRate = 147, onBalanceChange }) {
  const [tab, setTab] = useState("catalog"); // catalog | orders
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeParent, setActiveParent] = useState("");
  const [activeSub, setActiveSub] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(null); // service object
  const [refreshing, setRefreshing] = useState(false);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, sR] = await Promise.all([
        apiCall("get", "/smm/categories"),
        apiCall("get", `/smm/services${search ? `?search=${encodeURIComponent(search)}` : ""}`),
      ]);
      const cats = cR.data?.categories || [];
      setCategories(cats);
      setServices(sR.data?.services || []);
      // Auto-select first parent on initial load
      if (!activeParent) {
        const firstParent = cats.find(c => !c.parent_key);
        if (firstParent) setActiveParent(firstParent.key);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement du catalogue SMM");
    } finally { setLoading(false); }
  }, [apiCall, search, activeParent]);

  const fetchOrders = useCallback(async () => {
    try {
      const r = await apiCall("get", "/smm/my-orders");
      setOrders(r.data?.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement de vos commandes");
    }
  }, [apiCall]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);
  useEffect(() => { if (tab === "orders") fetchOrders(); }, [tab, fetchOrders]);

  // Auto-open the order modal if the user landed here from the public SMM
  // catalog with /client/smm?service=<id> deep-link.
  useEffect(() => {
    if (loading || services.length === 0 || ordering) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get("service");
      if (sid) {
        const svc = services.find(s => s.id === sid);
        if (svc) {
          setOrdering(svc);
          const url = new URL(window.location.href);
          url.searchParams.delete("service");
          window.history.replaceState({}, "", url.toString());
        }
      }
    } catch { /* ignore */ }
  }, [loading, services, ordering]);

  const topCategories = useMemo(() => categories.filter(c => !c.parent_key), [categories]);
  const subsByParent = useMemo(() => {
    const m = {};
    categories.filter(c => c.parent_key).forEach(c => {
      if (!m[c.parent_key]) m[c.parent_key] = [];
      m[c.parent_key].push(c);
    });
    return m;
  }, [categories]);

  const visibleServices = useMemo(() => {
    let list = services;
    if (activeSub) list = list.filter(s => s.category_key === activeSub);
    else if (activeParent) {
      const subKeys = (subsByParent[activeParent] || []).map(c => c.key);
      list = list.filter(s => s.category_key === activeParent || subKeys.includes(s.category_key));
    }
    return list;
  }, [services, activeParent, activeSub, subsByParent]);

  const handleParent = (k) => { setActiveParent(k); setActiveSub(""); };

  const handleOrderPlaced = async (newBalance) => {
    if (typeof newBalance === "number" && onBalanceChange) onBalanceChange(newBalance);
    setOrdering(null);
    await fetchOrders();
    setTab("orders");
  };

  const refreshOrders = async () => {
    setRefreshing(true);
    try { await fetchOrders(); } finally { setRefreshing(false); }
  };

  return (
    <div data-testid="client-smm-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          background: "linear-gradient(95deg, #4338CA 0%, #7C3AED 60%, #DB2777 100%)",
          color: "#FFF", padding: "1rem 1.1rem", borderRadius: 14,
          boxShadow: "0 6px 18px rgba(124,58,237,0.25)",
        }}
        data-testid="client-smm-hero"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Megaphone size={22} />
          <strong style={{ fontSize: "1.05rem" }}>SMM Catalogue — Social Media Marketing</strong>
        </div>
        <div style={{ fontSize: "0.8rem", opacity: 0.92, marginTop: 4 }}>
          Boostez vos comptes : abonnés, vues, likes, services premium IA, design, hébergement… Paiement instantané depuis votre solde.
        </div>
        <div style={{
          marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6,
          background: "rgba(255,255,255,0.18)", padding: "0.25rem 0.6rem",
          borderRadius: 999, fontSize: "0.78rem", fontWeight: 700,
        }} data-testid="client-smm-balance">
          <Wallet size={14} /> Solde dispo : {fmtUSD(clientBalanceHtg, exchangeRate)}
        </div>
      </div>

      {/* Inner tabs */}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          data-testid="client-smm-tab-catalog"
          onClick={() => setTab("catalog")}
          className="btn"
          style={{
            background: tab === "catalog" ? "#0F172A" : "#F1F5F9",
            color: tab === "catalog" ? "#FFF" : "#0F172A",
            padding: "0.45rem 0.85rem", fontSize: "0.85rem", fontWeight: 700,
          }}
        ><ShoppingBag size={14} /> Catalogue</button>
        <button
          data-testid="client-smm-tab-orders"
          onClick={() => setTab("orders")}
          className="btn"
          style={{
            background: tab === "orders" ? "#0F172A" : "#F1F5F9",
            color: tab === "orders" ? "#FFF" : "#0F172A",
            padding: "0.45rem 0.85rem", fontSize: "0.85rem", fontWeight: 700,
          }}
        ><Clock3 size={14} /> Mes commandes SMM</button>
      </div>

      {tab === "catalog" && (
        <>
          {/* Search bar */}
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "#94A3B8" }} />
            <input
              data-testid="client-smm-search"
              className="form-input"
              placeholder="Rechercher un service (followers, vues, hébergement…)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", paddingLeft: 32, fontSize: "0.88rem" }}
            />
          </div>

          {/* Parent categories pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="client-smm-parents">
            {topCategories.map(p => {
              const isActive = activeParent === p.key;
              return (
                <button
                  key={p.key}
                  data-testid={`client-smm-parent-${p.key}`}
                  onClick={() => handleParent(p.key)}
                  className="btn"
                  style={{
                    background: isActive ? "#7C3AED" : "#F1F5F9",
                    color: isActive ? "#FFF" : "#0F172A",
                    padding: "0.35rem 0.75rem", fontSize: "0.78rem", fontWeight: 700,
                    borderRadius: 999,
                  }}
                >{p.name}</button>
              );
            })}
          </div>

          {/* Sub-categories */}
          {activeParent && (subsByParent[activeParent] || []).length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: -4 }} data-testid="client-smm-subs">
              <button
                onClick={() => setActiveSub("")}
                data-testid="client-smm-sub-all"
                style={{
                  background: activeSub === "" ? "#0F172A" : "transparent",
                  color: activeSub === "" ? "#FFF" : "#475569",
                  border: "1px solid #CBD5E1", padding: "0.2rem 0.6rem",
                  fontSize: "0.72rem", fontWeight: 600, borderRadius: 999, cursor: "pointer",
                }}
              >Tous</button>
              {(subsByParent[activeParent] || []).map(s => {
                const isActive = activeSub === s.key;
                return (
                  <button
                    key={s.key}
                    data-testid={`client-smm-sub-${s.key}`}
                    onClick={() => setActiveSub(s.key)}
                    style={{
                      background: isActive ? "#0F172A" : "transparent",
                      color: isActive ? "#FFF" : "#475569",
                      border: "1px solid #CBD5E1", padding: "0.2rem 0.6rem",
                      fontSize: "0.72rem", fontWeight: 600, borderRadius: 999, cursor: "pointer",
                    }}
                  >{s.name}</button>
                );
              })}
            </div>
          )}

          {/* Services grid */}
          {loading && (
            <div style={{ padding: 16, color: "#64748B" }}>Chargement du catalogue…</div>
          )}
          {!loading && visibleServices.length === 0 && (
            <div style={{
              background: "#F8FAFC", border: "1px dashed #CBD5E1",
              padding: "1.5rem 1rem", borderRadius: 10, textAlign: "center", color: "#64748B",
            }} data-testid="client-smm-empty">
              <Megaphone size={28} style={{ opacity: 0.5 }} />
              <div style={{ marginTop: 6, fontWeight: 600 }}>Aucun service disponible dans cette catégorie.</div>
              <div style={{ fontSize: "0.78rem", marginTop: 2 }}>L&apos;admin ajoute régulièrement de nouveaux services. Revenez bientôt !</div>
            </div>
          )}
          {!loading && visibleServices.length > 0 && (
            <div
              data-testid="client-smm-services"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 10,
              }}
            >
              {visibleServices.map(s => (
                <div
                  key={s.id}
                  data-testid={`client-smm-service-${s.id}`}
                  style={{
                    background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12,
                    padding: "0.85rem 0.95rem", display: "flex", flexDirection: "column", gap: 6,
                  }}
                >
                  <strong style={{ color: "#0F172A", fontSize: "0.92rem", lineHeight: 1.3 }}>{s.name}</strong>
                  {s.description && (
                    <div style={{ fontSize: "0.76rem", color: "#64748B", lineHeight: 1.4 }}>{s.description}</div>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: "0.72rem", color: "#475569" }}>
                    <span style={{ background: "#F1F5F9", padding: "0.15rem 0.45rem", borderRadius: 6 }}>Qty {s.min_qty}–{s.max_qty}</span>
                    <span style={{ background: "#F1F5F9", padding: "0.15rem 0.45rem", borderRadius: 6 }}>Délai : {s.delivery_time}</span>
                  </div>
                  <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "#16A34A" }}>{fmtUSD(s.sale_price, exchangeRate)}</span>
                    <button
                      data-testid={`client-smm-buy-${s.id}`}
                      onClick={() => setOrdering(s)}
                      className="btn"
                      style={{
                        background: "#7C3AED", color: "#FFF",
                        padding: "0.4rem 0.8rem", fontSize: "0.78rem", fontWeight: 700,
                      }}
                    >Commander <ChevronRight size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "orders" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              data-testid="client-smm-orders-refresh"
              onClick={refreshOrders}
              className="btn"
              style={{ background: "#F1F5F9", padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
            ><RefreshCcw size={12} className={refreshing ? "spin" : ""} /> Rafraîchir</button>
          </div>
          {orders.length === 0 && (
            <div style={{
              background: "#F8FAFC", border: "1px dashed #CBD5E1",
              padding: "1.5rem 1rem", borderRadius: 10, textAlign: "center", color: "#64748B",
            }} data-testid="client-smm-no-orders">
              <ShoppingBag size={28} style={{ opacity: 0.5 }} />
              <div style={{ marginTop: 6, fontWeight: 600 }}>Aucune commande SMM pour l&apos;instant.</div>
              <div style={{ fontSize: "0.78rem", marginTop: 2 }}>Parcourez le catalogue et passez votre première commande !</div>
            </div>
          )}
          {orders.map(o => {
            const meta = STATUS_LABEL[o.status] || STATUS_LABEL.pending;
            const Icon = meta.Icon;
            return (
              <div
                key={o.id}
                data-testid={`client-smm-order-${o.id}`}
                style={{
                  background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 10,
                  padding: "0.75rem 0.95rem", display: "flex", flexDirection: "column", gap: 4,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                  <strong style={{ color: "#0F172A", fontSize: "0.92rem" }}>{o.service_name} × {o.quantity}</strong>
                  <span style={{
                    background: meta.bg, color: meta.color,
                    padding: "0.15rem 0.55rem", borderRadius: 999,
                    fontSize: "0.72rem", fontWeight: 700,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}><Icon size={11} /> {meta.label}</span>
                </div>
                <div style={{ fontSize: "0.78rem", color: "#475569", wordBreak: "break-all" }}>
                  Cible : <strong>{o.required_value}</strong>
                </div>
                <div style={{ fontSize: "0.74rem", color: "#64748B", display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <span>Total : <strong style={{ color: "#16A34A" }}>{fmtUSD(o.total_sale, exchangeRate)}</strong></span>
                  <span>{fmtDate(o.created_at)}</span>
                </div>
                {o.admin_note && (
                  <div style={{
                    fontSize: "0.74rem", color: "#1E293B", marginTop: 2,
                    background: "#F1F5F9", padding: "0.35rem 0.55rem", borderRadius: 6,
                  }}>
                    Note du support : {o.admin_note}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {ordering && (
        <OrderModal
          service={ordering}
          apiCall={apiCall}
          clientBalanceHtg={clientBalanceHtg}
          exchangeRate={exchangeRate}
          onClose={() => setOrdering(null)}
          onPlaced={handleOrderPlaced}
        />
      )}
    </div>
  );
}

function OrderModal({ service, apiCall, clientBalanceHtg, exchangeRate = 147, onClose, onPlaced }) {
  const [qty, setQty] = useState(service.min_qty || 1);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const total = useMemo(() => {
    const n = Math.max(service.min_qty || 1, Math.min(service.max_qty || 1e9, Number(qty || 0)));
    return Number(service.sale_price || 0) * n;
  }, [qty, service]);

  const insufficient = total > Number(clientBalanceHtg || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) { toast.error(`Veuillez saisir : ${service.required_label || "l'information requise"}`); return; }
    if (qty < service.min_qty || qty > service.max_qty) {
      toast.error(`Quantité entre ${service.min_qty} et ${service.max_qty}`);
      return;
    }
    if (insufficient) { toast.error("Solde insuffisant. Rechargez votre compte."); return; }
    setSubmitting(true);
    try {
      const r = await apiCall("post", "/smm/order", {
        service_id: service.id,
        quantity: Number(qty),
        required_value: value.trim(),
      });
      toast.success("Commande SMM enregistrée ! Notre équipe va la traiter.");
      // Notification WhatsApp instantanée à l'admin (ouverture dans un
      // nouvel onglet pour ne pas perturber le flux utilisateur).
      const adminWa = r.data?.admin_whatsapp_url;
      if (adminWa) {
        try { window.open(adminWa, "_blank", "noopener,noreferrer"); } catch {}
      }
      onPlaced(r.data?.new_balance);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la commande");
    } finally { setSubmitting(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      data-testid="client-smm-order-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#FFF", borderRadius: 14, padding: "1.1rem",
          width: "min(460px, 100%)", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: "1rem" }}>Commander : {service.name}</strong>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer" }}
            data-testid="client-smm-order-close"
          ><X size={18} /></button>
        </div>
        {service.description && (
          <div style={{ fontSize: "0.78rem", color: "#64748B", marginTop: 4 }}>{service.description}</div>
        )}
        <div style={{
          marginTop: 8, background: "#F8FAFC", padding: "0.5rem 0.65rem",
          borderRadius: 8, fontSize: "0.78rem", color: "#475569",
          display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6,
        }}>
          <span>Prix unitaire : <strong>{fmtUSD(service.sale_price, exchangeRate)}</strong></span>
          <span>Délai : <strong>{service.delivery_time}</strong></span>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155" }}>
            Quantité ({service.min_qty} – {service.max_qty})
            <input
              data-testid="client-smm-order-qty"
              type="number"
              min={service.min_qty}
              max={service.max_qty}
              value={qty}
              onChange={e => setQty(e.target.value)}
              className="form-input"
              style={{ marginTop: 4 }}
            />
          </label>

          {service.required_field !== "none" && (
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155" }}>
              {service.required_label || "Information requise"}
              <input
                data-testid="client-smm-order-value"
                className="form-input"
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder={service.required_placeholder || "Saisissez votre information"}
                style={{ marginTop: 4 }}
              />
            </label>
          )}

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "0.6rem 0.75rem", borderRadius: 10,
            background: insufficient ? "#FEE2E2" : "#DCFCE7",
            color: insufficient ? "#991B1B" : "#166534",
            fontWeight: 700,
          }} data-testid="client-smm-order-total">
            <span>Total à débiter</span>
            <span>{fmtUSD(total, exchangeRate)}</span>
          </div>
          {insufficient && (
            <div style={{ fontSize: "0.76rem", color: "#991B1B" }} data-testid="client-smm-order-insufficient">
              Solde actuel : {fmtUSD(clientBalanceHtg, exchangeRate)}. Rechargez votre compte pour finaliser.
            </div>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              className="btn"
              style={{ flex: 1, background: "#F1F5F9", padding: "0.6rem", fontWeight: 700 }}
            >Annuler</button>
            <button
              data-testid="client-smm-order-submit"
              type="submit"
              disabled={submitting || insufficient}
              className="btn"
              style={{
                flex: 2,
                background: submitting || insufficient ? "#94A3B8" : "#7C3AED",
                color: "#FFF", padding: "0.6rem", fontWeight: 700,
              }}
            ><Send size={14} /> {submitting ? "Envoi…" : "Confirmer la commande"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
